import {
  FunctionComponent,
  useEffect,
  useState,
  FormEvent,
  ChangeEvent,
} from "react";
import { Landmark } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../features/helpers";
import {
  createDepositAccount,
  fetchDepositAccountFormOptions,
} from "../../../store/DepositAccountSlice";
import {
  CreateDepositAccountDto,
  DepositAccountType,
  FiatTransferType,
} from "../../../models/DepositAccount";
import { Modal, FormField, PillTabs } from "../../ui";

interface Props {
  onClose: () => void;
}

type FieldKey =
  | "nickname"
  | "bank_id"
  | "agency"
  | "account"
  | "transfer_type"
  | "network_id"
  | "hash";

type Errors = Partial<Record<FieldKey, string>>;

const EMPTY_FORM = {
  nickname: "",
  bank_id: "",
  agency: "",
  account: "",
  transfer_type: "",
  network_id: "",
  hash: "",
};

const ModalAddDepositAccount: FunctionComponent<Props> = ({ onClose }) => {
  const dispatch = useAppDispatch();
  const loader = useAppSelector((s) => s.depositAccount.modalLoading);
  const formOptions = useAppSelector((s) => s.depositAccount.formOptions);

  const [type, setType] = useState<DepositAccountType>(DepositAccountType.FIAT);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [errors, setErrors] = useState<Errors>({});

  // Carrega bancos + redes para os dropdowns (uma vez).
  useEffect(() => {
    if (!formOptions) dispatch(fetchDepositAccountFormOptions());
  }, [formOptions, dispatch]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Troca de tipo limpa os campos especificos (mantem o apelido digitado).
  const switchType = (next: DepositAccountType) => {
    setErrors({});
    setForm({ ...EMPTY_FORM, nickname: form.nickname });
    setType(next);
  };

  const validate = (): Errors => {
    const e: Errors = {};
    if (!form.nickname.trim()) e.nickname = "Apelido obrigatorio";
    if (type === DepositAccountType.FIAT) {
      if (!form.bank_id) e.bank_id = "Selecione o banco";
      if (!form.agency.trim()) e.agency = "Agencia obrigatoria";
      if (!form.account.trim()) e.account = "Conta obrigatoria";
      if (!form.transfer_type) e.transfer_type = "Selecione TED ou PIX";
    } else {
      if (!form.network_id) e.network_id = "Selecione a rede";
      if (!form.hash.trim()) e.hash = "Hash da carteira obrigatoria";
    }
    return e;
  };

  const handleSubmit = (ev: FormEvent) => {
    ev.preventDefault();
    const v = validate();
    if (Object.keys(v).length > 0) {
      setErrors(v);
      return;
    }

    // Monta SOMENTE os campos do tipo escolhido (forbidNonWhitelisted no back).
    const payload: CreateDepositAccountDto =
      type === DepositAccountType.FIAT
        ? {
            type,
            nickname: form.nickname.trim(),
            bank_id: Number(form.bank_id),
            agency: form.agency.trim(),
            account: form.account.trim(),
            transfer_type: form.transfer_type as FiatTransferType,
          }
        : {
            type,
            nickname: form.nickname.trim(),
            network_id: Number(form.network_id),
            hash: form.hash.trim(),
          };

    dispatch(createDepositAccount(payload));
  };

  return (
    <Modal
      open
      onClose={onClose}
      icon={Landmark}
      title="Adicionar Conta de Deposito"
      subtitle="cadastre uma conta FIAT ou CRYPTO para a empresa receber depositos."
      maxWidth={480}
      footer={
        <button
          type="submit"
          form="modal-add-deposit-account-form"
          className="btn-pill primary"
          disabled={loader || undefined}
        >
          {loader ? <div className="loader" /> : "Adicionar"}
        </button>
      }
    >
      <form
        id="modal-add-deposit-account-form"
        onSubmit={handleSubmit}
        className="ui-stack"
      >
        <PillTabs
          tabs={[
            { key: DepositAccountType.FIAT, label: "FIAT" },
            { key: DepositAccountType.CRYPTO, label: "CRYPTO" },
          ]}
          value={type}
          onChange={(k) => switchType(k as DepositAccountType)}
          ariaLabel="Tipo de conta"
        />

        <FormField label="Apelido" htmlFor="nickname" error={errors.nickname}>
          <input
            id="nickname"
            name="nickname"
            placeholder="Ex.: Conta principal"
            value={form.nickname}
            onChange={handleChange}
          />
        </FormField>

        {type === DepositAccountType.FIAT ? (
          <>
            <FormField label="Banco" htmlFor="bank_id" error={errors.bank_id}>
              <select
                id="bank_id"
                name="bank_id"
                value={form.bank_id}
                onChange={handleChange}
              >
                <option value="">Selecione o banco</option>
                {formOptions?.banks.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </FormField>

            <div className="form-row">
              <FormField label="Agencia" htmlFor="agency" error={errors.agency}>
                <input
                  id="agency"
                  name="agency"
                  placeholder="0000"
                  value={form.agency}
                  onChange={handleChange}
                />
              </FormField>
              <FormField label="Conta" htmlFor="account" error={errors.account}>
                <input
                  id="account"
                  name="account"
                  placeholder="00000-0"
                  value={form.account}
                  onChange={handleChange}
                />
              </FormField>
            </div>

            <FormField
              label="Tipo"
              htmlFor="transfer_type"
              error={errors.transfer_type}
            >
              <select
                id="transfer_type"
                name="transfer_type"
                value={form.transfer_type}
                onChange={handleChange}
              >
                <option value="">Selecione</option>
                <option value={FiatTransferType.TED}>TED</option>
                <option value={FiatTransferType.PIX}>PIX</option>
              </select>
            </FormField>
          </>
        ) : (
          <>
            <FormField label="Hash da carteira" htmlFor="hash" error={errors.hash}>
              <input
                id="hash"
                name="hash"
                placeholder="0x..."
                value={form.hash}
                onChange={handleChange}
              />
            </FormField>

            <FormField label="Rede" htmlFor="network_id" error={errors.network_id}>
              <select
                id="network_id"
                name="network_id"
                value={form.network_id}
                onChange={handleChange}
              >
                <option value="">Selecione a rede</option>
                {formOptions?.networks.map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.name}
                  </option>
                ))}
              </select>
            </FormField>
          </>
        )}
      </form>
    </Modal>
  );
};

export default ModalAddDepositAccount;
